import React, { forwardRef, createContext, useContext } from "react";
import { BaseElementProps, PanelColor } from "../../props";
import { Panel, PanelProps } from "../Panel";
import { cn, springAnimationClass, normalAnimationClass } from "../../styles";
import { useDebugHook } from "../../helper";
import { Icon } from "../../atoms/Icon";
import { Divider } from "../../atoms/Divider";
import { mdiPlus, mdiChevronDown } from "@mdi/js";

export type AccordionIconType = "plus" | "chevron";

interface AccordionContextValue {
  openIds: Set<string | object>;
  toggleItem: (id: string | object) => void;
  type?: "single" | "multiple";
  iconType?: AccordionIconType;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

export interface AccordionItemProps extends Omit<PanelProps, "children"> {
  /**
   * Unique identifier for this accordion item (required when inside an Accordion).
   */
  id?: string;

  /**
   * Content displayed in the accordion header/summary.
   */
  summary: React.ReactNode;

  /**
   * Content displayed when the accordion item is expanded.
   */
  children?: React.ReactNode;

  /**
   * Whether the accordion item is open (controlled).
   * If provided, the item will not use Accordion context and must be controlled externally.
   */
  open?: boolean;

  /**
   * Callback fired when the open state changes (only used when directly controlled).
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Type of icon to display.
   * - "plus": Plus icon that rotates 45deg to form an X
   * - "chevron": Chevron down that rotates 180deg
   * Defaults to the parent Accordion's iconType, or "plus" if not specified.
   */
  iconType?: AccordionIconType;

  /**
   * Actions area displayed to the right of the summary (outside the summary element).
   * This allows for proper semantic HTML with interactive elements like buttons.
   */
  actions?: React.ReactNode;
}

export interface AccordionProps extends BaseElementProps {
  /**
   * Accordion items to render.
   */
  children?: React.ReactNode;

  /**
   * Type of accordion behavior.
   * - "single": Only one item can be open at a time
   * - "multiple": Multiple items can be open (default)
   */
  type?: "single" | "multiple";

  /**
   * Default open item IDs (only works with string IDs explicitly provided to AccordionItems).
   */
  defaultOpen?: string[];

  /**
   * Type of icon to display for all items.
   * - "plus": Plus icon that rotates 45deg to form an X (default)
   * - "chevron": Chevron down that rotates 180deg
   */
  iconType?: AccordionIconType;

  /**
   * When true, renders a Divider between each accordion item.
   */
  separated?: boolean;
}

/**
 * Individual accordion item component using native details/summary elements with Panel styling.
 */
export const AccordionItem = forwardRef<HTMLDetailsElement, AccordionItemProps>(
  (
    {
      id: providedId,
      summary,
      children,
      open,
      onOpenChange,
      iconType: itemIconType,
      actions,
      color = PanelColor.level0,
      ...panelProps
    },
    ref,
  ) => {
    const detailsRef = React.useRef<HTMLDetailsElement>(null);
    const context = useContext(AccordionContext);

    // Use provided ID or the element ref itself as the unique identifier
    const id = providedId ?? detailsRef;

    const iconType = itemIconType ?? context?.iconType ?? "chevron";

    // Use context only if item is not directly controlled (open === undefined)
    const isDirectlyControlled = open !== undefined;
    const usingContext = !isDirectlyControlled && context;

    // Single source of truth for open state
    const isOpen = isDirectlyControlled
      ? open
      : usingContext
        ? context.openIds.has(id)
        : false;

    // Animation state - controls the details element and content visibility
    const [detailsOpen, setDetailsOpen] = React.useState(isOpen);
    const [contentVisible, setContentVisible] = React.useState(isOpen);

    // Sync animation states when open state changes
    React.useEffect(() => {
      if (isOpen !== detailsOpen) {
        if (isOpen) {
          // Opening: set details open first, then animate content
          setDetailsOpen(true);
          requestAnimationFrame(() => setContentVisible(true));
        } else {
          // Closing: animate content first, then close details after animation
          setContentVisible(false);
          setTimeout(() => setDetailsOpen(false), 250);
        }
      }
    }, [isOpen, detailsOpen]);

    React.useImperativeHandle(ref, () => detailsRef.current!);

    const handleSummaryClick = (e: React.MouseEvent<HTMLElement>) => {
      // Prevent browser's default instant toggle
      e.preventDefault();

      if (usingContext) {
        // Using context: delegate to context
        context.toggleItem(id);
      } else if (isDirectlyControlled) {
        // Directly controlled: notify parent
        const newOpen = !isOpen;
        onOpenChange?.(newOpen);
      }
    };

    return (
      <Panel
        color={color}
        {...panelProps}
        className={cn(
          "accordion-item my-1 first:mt-0 last:mb-0",
          "transition-[margin,border-radius]",
          springAnimationClass,
          contentVisible && "accordion-item-open",
          !contentVisible &&
            "has-[+.accordion-item:not(.accordion-item-open)]:rounded-b-none has-[+.accordion-item:not(.accordion-item-open)]:mb-0",
          !contentVisible &&
            "[.accordion-item:not(.accordion-item-open)+&]:rounded-t-none [.accordion-item:not(.accordion-item-open)+&]:mt-0",
          panelProps.className,
        )}
      >
        <details ref={detailsRef} open={detailsOpen}>
          <summary
            className="list-none [&::-webkit-details-marker]:hidden select-none cursor-pointer flex items-center justify-between gap-3 pr-2"
            onClick={handleSummaryClick}
          >
            <div className="flex-1">{summary}</div>
            <Icon
              path={iconType === "chevron" ? mdiChevronDown : mdiPlus}
              className={cn(
                "transition-transform shrink-0",
                normalAnimationClass,
                contentVisible &&
                  (iconType === "chevron" ? "rotate-180" : "rotate-45"),
              )}
              size={20}
            />
          </summary>
          <div
            className={cn(
              "grid overflow-hidden transition-[grid-template-rows]",
              springAnimationClass,
              contentVisible ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="min-h-0">{children}</div>
          </div>
        </details>
      </Panel>
    );
  },
);

AccordionItem.displayName = "AccordionItem";

/**
 * Accordion component that groups collapsible sections.
 * Always provides context for child items.
 */
export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      className,
      children,
      type = "multiple",
      defaultOpen = [],
      iconType = "chevron",
      separated = false,
      ...props
    },
    ref,
  ) => {
    // Internal state for open items
    const [openIds, setOpenIds] = React.useState<Set<string | object>>(
      () => new Set(defaultOpen),
    );

    // Toggle function that handles both single and multiple modes
    const toggleItem = React.useCallback(
      (id: string | object) => {
        setOpenIds((prev) => {
          const newOpenIds = new Set(prev);

          if (newOpenIds.has(id)) {
            // Close the item
            newOpenIds.delete(id);
          } else {
            // Open the item
            if (type === "single") {
              // In single mode, close all other items
              newOpenIds.clear();
            }
            newOpenIds.add(id);
          }

          return newOpenIds;
        });
      },
      [type],
    );

    const contextValue = React.useMemo<AccordionContextValue>(
      () => ({
        openIds,
        toggleItem,
        type,
        iconType,
      }),
      [openIds, toggleItem, type, iconType],
    );

    const renderedChildren = separated
      ? React.Children.toArray(children).flatMap((child, i) =>
          i === 0 ? [child] : [<Divider key={`divider-${i}`} />, child],
        )
      : children;

    return (
      <AccordionContext.Provider value={contextValue}>
        <div ref={ref} className={cn("flex flex-col", className)} {...props}>
          {renderedChildren}
        </div>
      </AccordionContext.Provider>
    );
  },
);

Accordion.displayName = "Accordion";
